// src/lib/directus.js
// -----------------------------------------------------------------------------
// Directus SDK Client (REST + Session Authentication)
// -----------------------------------------------------------------------------
// This module provides:
//   • Directus client with secure session-based auth
//   • Custom endpoint helper with request/response hooks
//   • Logout helper
//   • File URL generator
//
// SECURITY:
// - Uses Cookies (HttpOnly) instead of frontend tokens.
// - `credentials: "include"` ensures secure cookie behavior.
// - No access tokens are stored in JS, reducing XSS risk.
// -----------------------------------------------------------------------------

import { createDirectus, rest, authentication } from "@directus/sdk";

/**
 * Base URL for Directus API.
 *
 * Fallbacks to dev path if environment variable isn't set.
 * @constant {string}
 */
export const DIRECTUS_URL =
  process.env.REACT_APP_DIRECTUS_INSTANCE_PATH || "http://localhost:8055";

/**
 * Create a Directus client using:
 *   - REST transport
 *   - Session authentication (uses cookies)
 *
 * @type {import("@directus/sdk").DirectusClient}
 */
export const directus = createDirectus(DIRECTUS_URL)
  .with(rest({ credentials: "include" }))
  .with(authentication("session", { credentials: "include" }));

// -----------------------------------------------------------------------------
// LOGOUT
// -----------------------------------------------------------------------------

/**
 * Logs out user using a custom auth service endpoint.
 *
 * @async
 * @returns {Promise<void>}
 */
export async function doLogout() {
  try {
    await customEP({
      path: "/user-auth/qa/otp/logout",
      method: "POST",
    });
  } catch (err) {
    console.error("Logout failed:", err);
    throw err;
  }
}

// -----------------------------------------------------------------------------
// CUSTOM ENDPOINT HELPERS
// -----------------------------------------------------------------------------

/**
 * Allowed HTTP methods for custom requests.
 * @typedef {'GET'|'SEARCH'|'POST'|'PUT'|'PATCH'|'DELETE'} HttpMethod
 */

/**
 * Options for a custom Directus endpoint request.
 *
 * @typedef {Object} RequestOptions
 * @property {string} path                      - Endpoint path (/items/table, etc.)
 * @property {HttpMethod} [method='GET']        - HTTP method
 * @property {Object<string, any>} [params]     - Query parameters
 * @property {Object<string, string>} [headers] - Custom headers
 * @property {string|FormData|Object} [body]    - Body payload
 * @property {RequestTransformer} [onRequest]   - Pre-request hook
 * @property {ResponseTransformer} [onResponse] - Post-response hook
 */

/**
 * Function to transform outgoing request before sending.
 * @callback RequestTransformer
 * @param {RequestInit} req
 * @returns {RequestInit|Promise<RequestInit>}
 */

/**
 * Function to transform the response after receiving it.
 * @callback ResponseTransformer
 * @param {any} responseData
 * @param {RequestInit} request
 * @returns {any|Promise<any>}
 */

/**
 * Execute a custom API request using Directus.
 *
 * Supports:
 *  - Query params
 *  - Headers
 *  - Raw or JSON body
 *  - Request/response transformers
 *
 * @async
 * @param {RequestOptions} config
 * @returns {Promise<any>}
 */
export async function customEP(config) {
  const {
    path,
    method = "GET",
    params,
    headers = {},
    body,
    onRequest,
    onResponse,
  } = config;

  // Build initial request object
  const req = {
    method,
    headers: {
      ...(body instanceof FormData
        ? {}
        : { "Content-Type": "application/json" }),
      ...headers,
    },
    body:
      body instanceof FormData ? body : body ? JSON.stringify(body) : undefined,
    params,
  };

  // Apply optional pre-request hook
  const finalRequest = onRequest ? await onRequest(req) : req;

  // Execute request using Directus .request()
  const response = await directus.request(() => ({
    path,
    method,
    params: finalRequest.params,
    headers: finalRequest.headers,
    body: finalRequest.body,
  }));

  // Apply optional post-response hook
  return onResponse ? await onResponse(response, finalRequest) : response;
}

// -----------------------------------------------------------------------------
// FILE URL UTIL
// -----------------------------------------------------------------------------

/**
 * Generate a public asset URL from a file ID or file object.
 *
 * @param {string|Object|null} file - File ID or { id: "xyz" }
 * @returns {string|null}
 *
 * @example
 * getFileUrl("abcd123");
 * // → "https://your-directus-url.com/assets/abcd123"
 *
 * @example
 * getFileUrl({ id: "abcd123" });
 * // → "https://your-directus-url.com/assets/abcd123"
 */
export function getFileUrl(file) {
  if (!file) return null;

  const fileId = typeof file === "string" ? file : file?.id;
  if (!fileId) return null;

  return `${DIRECTUS_URL}/assets/${fileId}`;
}
